import { type ReactNode, type ComponentProps, type RefObject, useRef } from 'react'
import styled from 'styled-components'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { NextLink } from '@/features/shared/elements/Link'
import { getCategoryUrl } from '@/features/cms/services/CMS/utils'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { Body } from '@shipt/design-system-typography'
import { Row } from '@shipt/design-system-layouts'
import { useNavOverflow } from '@/features/shop/components/CategorySubNav/utils/useNavOverflow'
import { type ShoppingCategory } from '@/features/shop/services/ShoppingCategories/types'
import { CATEGORY_HEADER_TYPE } from '@/features/shared/constants/global'
import {
  useIsUserZipOnlySignedInUser,
  useStoreId,
  useUser,
  useUserStoreName,
} from '@/features/account/services/User/hooks'
import { getRetailerDealsUrl, routes } from '@shared/constants/routes'
import { useIsPage } from '@/features/shared/hooks/useIsPage'
import { useSearchParams } from 'next/navigation'
import { useIsBrowse } from '@/features/shop/services/Search/hooks'
import { getKey, keys } from '@/features/shared/constants/keyCodes'
import { DynamicStoreInfoDrawer } from '@/features/shop/components/Drawer/StoreInfoDrawer/DynamicStoreInfoDrawer'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { TransparentButton } from '@/features/shared/elements/Buttons'
import { useQueryRewardsPrograms } from '@/features/account/services/RewardsPrograms/queries'
import { DynamicRewardsDrawer } from '@/features/shop/components/Drawer/RewardsDrawer/DynamicRewardsDrawer'
import { logWarn } from '@/features/shared/utils/logger'
import { useQueryShoppingStores } from '@/features/shop/services/ShoppingStore/queries'
import { AiIcon, type SystemIconType } from '@shipt/design-system-icons'
import { useExperiment } from '@/features/shared/services/Experiments/hooks'
import { ExperimentType } from '@shared/types/experiments'
import { ActivityPunchCardNavigationItem } from '@/features/shop/components/CategorySubNav/ActivityPunchCardNavigationItem'
import { useQueryRetailerETA } from '@/features/shop/services/DeliveryWindows/queries'
import { range } from '@/features/shared/utils/range'
import { Skeleton } from '@shipt/design-system-loading'
import {
  useQueryCatNavCMS,
  useQueryCMSActivityPunchCard,
} from '@/features/cms/services/CMS/queries'
import { useSession } from '@/features/authentication/utils/authentication/hooks/useSession'
import { MORE_CATEGORY_ITEM_ID } from '@/features/shop/components/CategorySubNav/MoreCategoryItem'

const itemPadding = 12

const { DEALS, BUY_AGAIN, LISTS, STORE_PRICING, MORE, STORE_REWARDS, CART_BUILDER } =
  CATEGORY_HEADER_TYPE

type Props = {
  subNavRef: RefObject<HTMLDivElement | null>
  navCategories: ShoppingCategory[]
  moreCategories: ShoppingCategory[]
  openDrawer: (option: string) => void
  setItemsToDisplay: (amount: number) => void
  itemsToDisplay: number
  onCategorySelect: (category: ShoppingCategory, col: number, row?: number) => void
  selectedOption: string
  focusOnFirstCategory: () => void
}

export const RetailerPriorityNavigation = ({
  subNavRef,
  navCategories,
  moreCategories,
  openDrawer,
  setItemsToDisplay,
  onCategorySelect,
  selectedOption,
  itemsToDisplay = 0,
  focusOnFirstCategory,
}: Props) => {
  const user = useUser()
  const storeName = useUserStoreName()
  const isZipOnlyUser = useIsUserZipOnlySignedInUser()
  const isPage = useIsPage()
  const searchParams = useSearchParams()
  const categoryId = searchParams?.get('category_id')
  const isBrowse = useIsBrowse()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const rowRef = useRef<HTMLDivElement>(null)
  const { openDialog } = useHandleDialog()
  const { data: userRewardsPrograms } = useQueryRewardsPrograms()
  const storeId = useStoreId()
  const storesQuery = useQueryShoppingStores()
  const delivery_stores = storesQuery.data?.delivery_stores
  const { active: aiCartBuilderInternalEnabled } = useExperiment(
    ExperimentType.AI_CART_BUILDER_ROLLOUT_INTERNAL,
    { autoTrack: true },
  )
  const { active: aiCartBuilderExternalEnabled } = useExperiment(
    ExperimentType.AI_CART_BUILDER_ROLLOUT_EXTERNAL,
    { autoTrack: true },
  )
  const aiCartBuilderEnabled = aiCartBuilderInternalEnabled || aiCartBuilderExternalEnabled

  const punchCardQuery = useQueryCMSActivityPunchCard()
  const punchCardData = punchCardQuery.data

  const retailerETAQuery = useQueryRetailerETA()
  const categoriesQuery = useQueryCatNavCMS()

  // every query below is enabled off the session and the user data
  const { isSessionLoading, isFullyAuthenticated } = useSession()
  const isUserSettled = !isSessionLoading && (!isFullyAuthenticated || Boolean(user?.id))
  // the row is measured for overflow once, so anything that can add or drop an item has to
  // settle first or the visible count is measured against a width that is about to change
  const isNavReady =
    isUserSettled &&
    [storesQuery, categoriesQuery, retailerETAQuery, punchCardQuery].every(
      (query) => !query.isEnabled || !query.isPending,
    )

  const visibleWidth = useNavOverflow({
    rowRef,
    containerRef: subNavRef,
    // zero until the real items replace the skeleton, so nothing is measured early
    itemCount: isNavReady ? navCategories.length : 0,
    onChange: setItemsToDisplay,
  })

  type HeaderLink = {
    name: string
    label?: string
    icon?: SystemIconType
    url: string
    isSelected: boolean
  }

  const aiCartBuilderLink: HeaderLink[] = aiCartBuilderEnabled
    ? [
        {
          name: CART_BUILDER,
          label: 'Ask Shipt',
          icon: AiIcon,
          url: routes.CART_BUILDER.url,
          isSelected: selectedOption === CART_BUILDER || isPage.CART_BUILDER,
        },
      ]
    : []

  const baseHeaderLinks: HeaderLink[] = [
    ...(user?.id
      ? [
          ...aiCartBuilderLink,
          {
            name: BUY_AGAIN,
            url: routes.BUY_AGAIN.url,
            isSelected: selectedOption === BUY_AGAIN || isPage.BUY_AGAIN,
          },
          {
            name: LISTS,
            url: routes.LISTS_HUB.url,
            isSelected: selectedOption ? selectedOption === LISTS : isPage.LIST || isPage.LISTS_HUB,
          },
        ]
      : []),
    {
      name: DEALS,
      url: getRetailerDealsUrl(storeName),
      isSelected: selectedOption ? selectedOption === DEALS : isPage.RETAILER_DEALS,
    },
  ]

  const handleHeaderItemClick = (type: string, index: number, url: string) => {
    trackElementClicked({
      type: 'link',
      display_format: DISPLAY_FORMAT.HORIZONTAL,
      display_sublocation: 'header navigation',
      list_index: 0,
      column_index: index,
      link_name: type,
      content: type,
      next_location: url,
    })
  }

  const handleStorePricingClick = () => {
    trackElementClicked({
      type: 'button',
      display_format: DISPLAY_FORMAT.HORIZONTAL,
      display_sublocation: 'header navigation',
      list_index: 0,
      column_index: itemsToDisplay + 4,
      link_name: STORE_PRICING,
      content: STORE_PRICING,
    })
    openDialog(DynamicStoreInfoDrawer, {
      defaultTab: 'storeInfo',
    })
  }

  const rewardProgram = userRewardsPrograms?.available_programs.find(
    (availablePrograms) => storeId === availablePrograms.store.id,
  )
  const existingRewardProgram = userRewardsPrograms?.current_programs.find(
    (programs) => storeId === programs.store.id,
  )
  const program = existingRewardProgram || rewardProgram

  const storeHasLoyaltyProgram = delivery_stores?.find(
    (store) => store.id === storeId,
  )?.has_loyalty_program

  // the whole row waits on every query behind it, so the items land in
  // place once instead of popping in one at a time.
  if (!isNavReady) {
    return (
      <NavigationWrapper justify="start" align="start" ref={rowRef}>
        <NavigationSkeleton />
      </NavigationWrapper>
    )
  }
  return (
    <NavigationWrapper justify="start" align="start" ref={rowRef}>
      {baseHeaderLinks.map(({ name, label, url, isSelected, icon: Icon }, index) => (
        <HeaderItemLink
          href={url}
          onMouseEnter={() => openDrawer(name)}
          onFocus={() => openDrawer(name)}
          key={name}
        >
          <HeaderItem
            isSelected={isSelected}
            onClick={() => handleHeaderItemClick(name, index, url)}
          >
            <Row align="center" spacing="xs">
              {!!Icon && <Icon size="sm" />}
              <span>{label ?? name}</span>
            </Row>
          </HeaderItem>
        </HeaderItemLink>
      ))}
      <CategoryItems ref={subNavRef} $visibleWidth={visibleWidth}>
        {navCategories.map((category, col) => {
          const { url_slug, name, id } = category
          if (!url_slug || !name) return null
          const isSelected = selectedOption === name
          const isOnCategoryPage =
            id === Number(categoryId) && !selectedOption && isBrowse && !isPage.BUY_AGAIN
          const categoryUrl = getCategoryUrl(category, storeName)
          // clipped by the container, so hide it from assistive tech and tab order too
          const isClipped = col >= itemsToDisplay

          const handleOpenDrawer = () => openDrawer(name)

          const handleMouseEnterOrFocus = () => {
            timeoutRef.current = setTimeout(handleOpenDrawer, 100)
          }
          const clearHoverTimeout = () => {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current)
              timeoutRef.current = null
            }
          }
          return (
            <HeaderItemLink
              key={id}
              href={categoryUrl}
              prefetch={false}
              aria-expanded={isSelected || isOnCategoryPage}
              aria-hidden={isClipped}
              tabIndex={isClipped ? -1 : undefined}
              onClick={(e) => {
                e.preventDefault()
                handleHeaderItemClick(name, col + baseHeaderLinks.length, categoryUrl)
                onCategorySelect(category, col + baseHeaderLinks.length)
              }}
              onMouseEnter={handleMouseEnterOrFocus}
              onMouseLeave={clearHoverTimeout}
              onFocus={handleMouseEnterOrFocus}
              onBlur={clearHoverTimeout}
              onKeyDown={(e) => {
                if (getKey(e) === keys.ARROW_DOWN) {
                  e.preventDefault()
                  focusOnFirstCategory()
                }
              }}
            >
              <HeaderItem isSelected={isSelected}>{name}</HeaderItem>
            </HeaderItemLink>
          )
        })}
      </CategoryItems>
      {moreCategories.length > 0 && (
        <HeaderItemContainer
          onMouseEnter={() => openDrawer(MORE)}
          onFocus={() => openDrawer(MORE)}
          onKeyDown={(e) => {
            if (getKey(e) === keys.ARROW_DOWN) {
              e.preventDefault()
              // focus on the first category item in the drawer
              document.getElementById(MORE_CATEGORY_ITEM_ID)?.focus()
            }
          }}
        >
          <HeaderItem isSelected={selectedOption === MORE}>{MORE}</HeaderItem>
        </HeaderItemContainer>
      )}
      <StorePricingAndRewardsButton
        onMouseEnter={() => openDrawer(STORE_PRICING)}
        onFocus={() => openDrawer(STORE_PRICING)}
        onClick={handleStorePricingClick}
      >
        <HeaderItem isSelected={selectedOption === STORE_PRICING}>{STORE_PRICING}</HeaderItem>
      </StorePricingAndRewardsButton>
      {!!storeHasLoyaltyProgram && !isZipOnlyUser && (
        <StorePricingAndRewardsButton
          onMouseEnter={() => openDrawer(STORE_REWARDS)}
          onFocus={() => openDrawer(STORE_REWARDS)}
          onClick={() => {
            if (program) {
              trackElementClicked({
                next_location: 'LoyaltyProgramForm',
                link_name: 'store_rewards',
                type: 'button',
              })
              openDialog(DynamicRewardsDrawer, {
                rewardProgram: program,
                mode: existingRewardProgram ? 'edit' : 'add',
              })
            } else {
              logWarn('Loyalty program not found')
            }
          }}
        >
          <HeaderItem isSelected={selectedOption === STORE_REWARDS}>{STORE_REWARDS}</HeaderItem>
        </StorePricingAndRewardsButton>
      )}
      {!!punchCardData?.data?.callout && (
        <ActivityPunchCardNavigationItem punchCardData={punchCardData} />
      )}
    </NavigationWrapper>
  )
}

const NavigationSkeleton = () => (
  <>
    {range(12).map((index) => (
      <PlaceholderSlot key={index}>
        <Skeleton height={16} width={70} />
      </PlaceholderSlot>
    ))}
  </>
)

const HEADER_ITEM_HEIGHT = '3rem'

// same box a nav item occupies, so the skeletons are the same size and spacing as the real items
const PlaceholderSlot = styled.div`
  display: flex;
  align-items: center;
  height: ${HEADER_ITEM_HEIGHT};
  padding: 0 ${itemPadding}px;
`

const NavigationWrapper = styled(Row)`
  height: 100%;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`

// holds every category, whatever does not fit in the leftover space is clipped
const CategoryItems = styled(Row)<{ $visibleWidth: number }>`
  min-width: 0;
  overflow: hidden;
  width: ${({ $visibleWidth }) => $visibleWidth && `${$visibleWidth}px`};
`

const HeaderItemLink = styled(NextLink)`
  padding: 0 ${itemPadding}px;

  &[aria-hidden='true'] {
    visibility: hidden;
  }
`

const HeaderItemText = styled(Body)`
  position: relative;
  height: ${HEADER_ITEM_HEIGHT};
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    font-weight: 600;
  }
`

const StorePricingAndRewardsButton = styled(TransparentButton)`
  padding: 0 ${itemPadding}px;

  /* stylelint-disable-next-line plugin/enforce-exact-media-queries -- Currently overriding stylelint rule due to the very customized display functionality of the Retailer Header's category nav items requested by Design during Experience Refresh. Changing this to a nearer Astro breakpoint would significantly alter which category nav items are shown */
  @media (max-width: 1099px) {
    display: none;
  }
`

const HeaderItemContainer = styled.button`
  padding: 0 ${itemPadding}px;
  background-color: inherit;
  border: none;
`

const HeaderItem = ({
  isSelected = false,
  children,
  ...restProps
}: {
  isSelected?: boolean
  children: ReactNode
  onClick?: ComponentProps<'span'>['onClick']
}) => {
  return (
    <HeaderItemText as="span" strong={isSelected} variant="primary" size="md" {...restProps}>
      {children}
    </HeaderItemText>
  )
}
